<template>
    <div class="row p-3">
        <input-text 
            class="col-12 col-md-3" 
            label="Ano de inicio" 
            :min="1900" 
            type="number" 
            :max-length="4" 
            v-model="form.anoInicio"
            ref="anoInicio"
            required
            :readonly="readonly" />

        <input-text 
            class="col-12 col-md-3" 
            label="Código interno" 
            type="number" 
            :max-length="4" 
            v-model="form.codigoInterno"
            ref="codigoInterno"
            required 
            :readonly="readonly" />

        <input-select
            class="col-12 col-md-3"
            label="Tipo de projeto"
            :options="tiposProjeto"
            v-model="form.tipoProjeto"
            ref="tipoProjeto"
            required
            :disabled="readonly" />

        <input-text 
            class="col-12 col-md-3"
            label="Duração do projeto"
            type="number"
            :min="1"
            :max="duracaoMaxima"
            :max-length="2"
            v-model="duracaoProjeto"
            ref="duracao"
            required
            :readonly="readonly" />

        <input-money
            class="col-12 col-md-3"
            label="Limite de Investimento"
            v-model="form.valorInvestir"
            ref="valor"
            required
            :readonly="readonly"
        >
            <template #label>
                <div>
                    <span>Limite de investimento</span>
                    <b-badge class="ml-2">Máx. 10%</b-badge>
                </div>
            </template>
        </input-money>

        <div class="col-12">
            <b-button 
                variant="success" 
                v-if="permissao.c && !readonly" 
                @click="salvar"
            >
                <i class="fas fa-check mr-2"></i>
                Salvar
            </b-button>
        </div>
    </div>
</template>

<script>
import InputText from '@/components/inputs/InputText.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputMoney from '@/components/inputs/InputMoney.vue';

import cloneDeep from 'lodash/cloneDeep';
import helpers from '@/common/utils/helpers';

import { FormMixin } from '@/common/mixins/FormMixin';
import { AlertasMixin } from '@/common/mixins/AlertasMixin';

import { START_LOADING, STOP_LOADING } from "@/store/Store";
import ProjetoGestaoService from '@/common/services/projeto_gestao/projeto_gestao.service';

export default {
    name: 'Detalhes',

    mixins: [FormMixin, AlertasMixin],

    components: {
        InputText,
        InputMoney,
        InputSelect
    },

    props: {
        projeto: {
            type: Object,
            default: null
        },
    },

    data: () => ({
        form: {
            tipoProjeto: null,
            anoInicio: null,
            codigoInterno: null,
            duracao: null,
            valorInvestir: null,
        },

        tiposProjeto: [
            { value: 'GESTAO', text: 'Gestão' },
            { value: 'GPPDI', text: 'GPPDI' },
        ],
    }),

    computed: {
        empresaSelecionada() {
            return this.$store.state.app.selectedCompanyObj;
        },

        readonly() {
            return this.projeto != null;
        },

        permissao() {
            return this.$store.getters['app/retornaPermissao']('ProjetosGestao');
        },

        duracaoMaxima() {
            if (this.form.tipoProjeto === 'GPPDI') {
                return 63;
            }

            if (this.form.tipoProjeto === 'GESTAO') {
                return 24;
            }

            return 24;
        },

        duracaoProjeto: {
            get() {
                if (this.form.duracao === null || this.form.duracao === undefined) {
                    return '';
                }

                return String(this.form.duracao);
            },

            set(valor) {
                if (valor === null || valor === undefined || valor === '') {
                    this.form.duracao = null;
                    return;
                }

                const valorLimpo = String(valor).replace(/\D/g, '');

                if (!valorLimpo) {
                    this.form.duracao = null;
                    return;
                }

                const duracao = Number(valorLimpo);

                if (duracao < 1) {
                    this.form.duracao = null;
                    return;
                }

                if (duracao > this.duracaoMaxima) {
                    this.form.duracao = String(this.duracaoMaxima);
                    return;
                }

                this.form.duracao = String(duracao);
            }
        },
    },

    methods: {
        normalizarTipoProjeto(tipoProjeto, duracao) {
            const duracaoNumerica = Number(duracao);

            if (duracaoNumerica > 24) {
                return 'GPPDI';
            }

            if (tipoProjeto === 1 || tipoProjeto === '1') {
                return 'GPPDI';
            }

            if (tipoProjeto === 2 || tipoProjeto === '2') {
                return 'GESTAO';
            }

            const tipoNormalizado = String(tipoProjeto || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toUpperCase();

            if (tipoNormalizado === 'GPPDI') {
                return 'GPPDI';
            }

            if (tipoNormalizado === 'GESTAO') {
                return 'GESTAO';
            }

            return null;
        },

        validarDuracaoProjeto() {
            const duracao = Number(this.form.duracao);
            const isGPPDI = this.form.tipoProjeto === 'GPPDI';
            const nomeProjeto = isGPPDI ? 'GPPDI' : 'gestão';

            if (!duracao || duracao < 1) {
                this.alertaErro("Informe uma duração válida para o projeto!");
                return false;
            }

            if (duracao > this.duracaoMaxima) {
                this.alertaErro(
                    `A duração máxima para projeto ${nomeProjeto} é de ${this.duracaoMaxima} meses!`
                );
                return false;
            }

            return true;
        },

        salvar() {
            if (!this.validateState()) {
                return;
            }

            if (!this.validarDuracaoProjeto()) {
                return;
            }

            const isGPPDI = this.form.tipoProjeto === 'GPPDI';
            const nomeProjeto = isGPPDI ? 'GPPDI' : 'gestão';

            this.confirmar(`Deseja salvar o projeto ${nomeProjeto}?`).then(confirmado => {
                if (!confirmado) {
                    return;
                }
                let form = cloneDeep(this.form);

                form.anoInicio = Number(form.anoInicio);
                form.duracao = Number(form.duracao);
                form.valorInvestir = helpers.removerValor(form.valorInvestir);
                form.empresaId = this.empresaSelecionada.id;
                form.tipoProjetoPesquisaEnum = isGPPDI ? 'GPPDI' : 'Gestao';

                this.$store.dispatch(START_LOADING);

                ProjetoGestaoService.salvarDetalhes(form).then(res => {
                    this.alertaSucesso(`Projeto ${nomeProjeto} salvo com sucesso!`).then(() => {
                        this.$router.push({
                            name: "projeto-gestao-proponente-detalhamento",
                            params: { id: res.data.data }
                        });
                    });
                }).catch(() => {
                    this.alertaErro(`Erro ao salvar o projeto ${nomeProjeto}!`);
                }).finally(() => {
                    this.$store.dispatch(STOP_LOADING);
                });
            });
        }
    },

    watch: {
        projeto: {
            handler(projeto) {
                if (!projeto) {
                    return;
                }

                let proj = cloneDeep(projeto);

                if (projeto.projetoGestao) {
                    proj = cloneDeep(projeto.projetoGestao);
                }

                const tipoProjeto = this.normalizarTipoProjeto(
                    proj.tipoProjeto || proj.tipoProjetoPesquisaEnum,
                    proj.duracao
                );

                this.form.tipoProjeto = tipoProjeto;

                this.form.anoInicio = proj.anoInicio != null 
                    ? String(proj.anoInicio) 
                    : null;

                this.form.codigoInterno = proj.codigoInterno != null 
                    ? String(proj.codigoInterno) 
                    : null;

                this.form.duracao = proj.duracao != null 
                    ? String(proj.duracao) 
                    : null;

                this.form.valorInvestir = helpers.formatarValor(proj.valorInvestir);
            },
            deep: true,
            immediate: true
        },

        'form.tipoProjeto': function() {
            const duracao = Number(this.form.duracao);

            if (duracao && duracao > this.duracaoMaxima) {
                this.form.duracao = String(this.duracaoMaxima);
            }
        },
    }
};
</script>

<style lang="">
</style>